import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { ArrowLeft, ArrowRight, Bell, Bot, Check, CheckCircle2, Copy, Inbox, Mail, PartyPopper, Plus, Sparkles, Trash2, Users } from 'lucide-react';
import { api } from '../lib/api';
import { qk } from '../lib/keys';
import { bridge } from '../lib/desktop';
import { copyText } from '../lib/clipboard';
import { useMe, useUpdateSettings } from '../hooks/useMe';
import { useMailboxes } from '../hooks/useMailboxes';
import { inviteLink, useCurrentWorkspace } from '../hooks/useWorkspace';
import { useLocalStorage } from '../hooks/useBrowser';
import { useRing } from '../lib/ring';
import { AccountsAddSection } from '../components/settings/AccountsAddSection';
import { useOAuthConnect } from '../components/settings/AccountsOAuth';
import { AiConsentCard } from '../components/settings/AiConsentPrivacy';
import { AiProviderEditor } from '../components/settings/AiProviderEditor';
import { Badge, Button, Card, CardBody, CardHeader, Checkbox, Notice, Spinner, Switch, useToast } from '../components/ui';
import { spring } from '../components/ui/motion';
import { BrandMark } from '../components/ui/BrandMark';
import { WorkspaceMark } from '../components/layout/WorkspaceSwitcher';

type StepId = 'welcome' | 'email' | 'ai' | 'notifications' | 'team' | 'done';

const STEP_META: Record<StepId, { label: string; icon: ReactNode }> = {
  welcome: { label: 'Welcome', icon: <Sparkles className="h-4 w-4" /> },
  email: { label: 'Email', icon: <Mail className="h-4 w-4" /> },
  ai: { label: 'AI assistant', icon: <Bot className="h-4 w-4" /> },
  notifications: { label: 'Notifications', icon: <Bell className="h-4 w-4" /> },
  team: { label: 'Your team', icon: <Users className="h-4 w-4" /> },
  done: { label: 'Done', icon: <Check className="h-4 w-4" /> },
};

/** Clears demo mailboxes and the Demo AI provider (owners/admins only). */
function useDemoCleanup() {
  const qc = useQueryClient();
  const mailboxes = useMailboxes().data?.mailboxes ?? [];
  const ai = useQuery({ queryKey: qk.aiProviders, queryFn: () => api('GET /api/ai/providers') });
  const demoMailboxes = mailboxes.filter((m) => m.isDemo);
  const demoAi = (ai.data?.providers ?? []).filter((p) => p.kind === 'demo');
  const remove = useMutation({
    mutationFn: async () => {
      for (const m of demoMailboxes) await api('DELETE /api/mailboxes/:id', { params: { id: m.id }, query: { purge: '1' } });
      for (const p of demoAi) await api('DELETE /api/ai/providers/:id', { params: { id: p.id } });
    },
    onSettled: () => {
      void qc.invalidateQueries();
    },
  });
  return { count: demoMailboxes.length + demoAi.length, demoMailboxes, demoAi, remove };
}

function StepFrame({ title, description, children }: { title: string; description: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
        <p className="mt-1.5 max-w-xl text-[0.875rem] text-muted">{description}</p>
      </header>
      {children}
    </div>
  );
}

export function WelcomePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe().data;
  const update = useUpdateSettings();
  const { workspace, isOrg, isAdmin } = useCurrentWorkspace();
  const mailboxes = useMailboxes();
  const oauth = useOAuthConnect();
  const ring = useRing();
  const demo = useDemoCleanup();
  const [removeDemo, setRemoveDemo] = useState(true);

  const steps = useMemo<StepId[]>(() => ['welcome', 'email', ...(isAdmin ? (['ai'] as const) : []), 'notifications', ...(isOrg && isAdmin ? (['team'] as const) : []), 'done'], [isAdmin, isOrg]);
  // Remembered so a Gmail/Outlook sign-in that leaves the page comes back to the same step.
  const [stepId, setStepId] = useLocalStorage<StepId>(`mp:welcome-step:${me?.user.id ?? ''}`, 'welcome');
  const index = Math.max(0, steps.indexOf(stepId));
  const step = steps[index];
  const go = (d: 1 | -1) => setStepId(steps[Math.min(steps.length - 1, Math.max(0, index + d))]);

  const ai = useQuery({ queryKey: qk.aiProviders, queryFn: () => api('GET /api/ai/providers'), enabled: isAdmin });
  const [aiEditor, setAiEditor] = useState(false);
  const [invite, setInvite] = useState<{ code: string } | null>(null);
  const createInvite = useMutation({
    mutationFn: () => api('POST /api/workspace/invites', { body: { role: 'member', expiresInDays: 14, maxUses: 25 } }),
    onSuccess: (r) => setInvite({ code: r.invite.code }),
    onError: (e) => toast.error(e),
  });

  const finish = async (to = '/inbox') => {
    await update.mutateAsync({ onboardedAt: new Date().toISOString() });
    setStepId('welcome');
    navigate(to, { replace: true });
  };

  const firstName = (me?.user.displayName ?? '').split(' ')[0] || 'there';
  const realMailboxes = (mailboxes.data?.mailboxes ?? []).filter((m) => !m.isDemo || !removeDemo);
  const providers = (mailboxes.data?.providers ?? []).filter((p) => p.provider !== 'demo');
  const n = me?.settings.notifications;

  useEffect(() => {
    // Clear the step memory once setup is done (e.g. finished on another device).
    if (me?.settings.onboardedAt) setStepId('welcome');
  }, [me?.settings.onboardedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = async () => {
    if (step === 'welcome' && removeDemo && demo.count && isAdmin) {
      try {
        await demo.remove.mutateAsync();
        toast.success('Demo data removed');
      } catch (e) {
        toast.error(e);
        return;
      }
    }
    go(1);
  };

  return (
    <div className="mp-canvas min-h-screen min-h-[100dvh]">
      <div className="mx-auto flex max-w-3xl flex-col px-4 pb-16 pt-6 sm:px-6">
        <div className="mb-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <BrandMark size={36} />
            <span className="text-lg font-semibold text-ink">MailPilot</span>
          </div>
          {step !== 'done' && (
            <button type="button" onClick={() => void finish()} className="rounded-lg px-2 py-1 text-[0.8125rem] font-medium text-muted hover:bg-raised hover:text-ink">
              Skip setup
            </button>
          )}
        </div>

        <ol className="mb-8 flex items-center gap-1.5" aria-label="Setup progress">
          {steps.map((s, i) => (
            <li key={s} className="flex flex-1 flex-col gap-1.5">
              <span className={clsx('h-1.5 rounded-full transition-colors duration-300', i <= index ? 'bg-primary' : 'bg-line')} />
              <span className={clsx('hidden items-center gap-1 text-[0.6875rem] font-medium sm:flex', i === index ? 'text-primary-ink' : 'text-subtle')} aria-current={i === index ? 'step' : undefined}>
                {STEP_META[s].icon} {STEP_META[s].label}
              </span>
            </li>
          ))}
        </ol>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={spring}>
            {step === 'welcome' && (
              <StepFrame
                title={`Welcome to MailPilot, ${firstName}`}
                description="Let's get you set up in a couple of minutes: connect your email, choose how the AI helps, and decide how you're notified. You can change everything later in Settings."
              >
                {workspace && (
                  <Card>
                    <CardBody className="flex items-center gap-4">
                      <WorkspaceMark ws={workspace} size={48} />
                      <div className="min-w-0">
                        <p className="text-[0.9375rem] font-semibold text-ink">{workspace.name}</p>
                        <p className="text-[0.8125rem] text-muted">
                          {isOrg
                            ? `Organization · you're ${workspace.role === 'member' ? 'a member' : `the ${workspace.role}`}. Email accounts here are shared with your team.`
                            : 'Personal workspace · only you can see the accounts you connect.'}
                        </p>
                      </div>
                    </CardBody>
                  </Card>
                )}
                {demo.count > 0 && isAdmin && (
                  <Card>
                    <CardBody className="space-y-3">
                      <p className="flex items-center gap-2 font-semibold text-ink">
                        <Trash2 className="h-4 w-4 text-muted" /> Start clean
                      </p>
                      <p className="text-[0.8125rem] text-muted">
                        This workspace has demo data from trying MailPilot out:{' '}
                        {[demo.demoMailboxes.length && `${demo.demoMailboxes.length} demo mailbox${demo.demoMailboxes.length === 1 ? '' : 'es'}`, demo.demoAi.length && 'the Demo AI']
                          .filter(Boolean)
                          .join(' and ')}
                        .
                      </p>
                      <Checkbox checked={removeDemo} onChange={setRemoveDemo} label="Remove the demo data when I continue" description="Its sample emails, drafts and reminders are deleted. Your real accounts aren't touched." />
                    </CardBody>
                  </Card>
                )}
              </StepFrame>
            )}

            {step === 'email' && (
              <StepFrame
                title="Connect your email"
                description={isOrg ? 'Add the inboxes your team works from — shared addresses like info@ or support@, or your own. You can add more any time.' : 'Add the addresses you use. MailPilot brings them together in one inbox and keeps each one separate when you reply.'}
              >
                {realMailboxes.length > 0 && (
                  <Card>
                    <CardHeader icon={<Inbox className="h-5 w-5" />} title="Connected" description="Recent mail is imported first; older history follows in the background." />
                    <CardBody className="space-y-2">
                      {realMailboxes.map((m) => (
                        <div key={m.id} className="flex items-center gap-3 rounded-xl bg-raised px-3 py-2.5">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: m.color }} />
                          <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-medium text-ink">{m.email}</span>
                          {m.status === 'active' ? <Badge tone="success" icon={<CheckCircle2 className="h-3 w-3" />}>Connected</Badge> : <Badge tone="warn">{m.status.replace('_', ' ')}</Badge>}
                        </div>
                      ))}
                    </CardBody>
                  </Card>
                )}
                {providers.length > 0 ? (
                  <AccountsAddSection providers={providers} oauth={oauth} emptyState={!realMailboxes.length} />
                ) : (
                  <Spinner label="Loading" />
                )}
              </StepFrame>
            )}

            {step === 'ai' && (
              <StepFrame
                title="Set up your AI assistant"
                description="MailPilot uses your own AI provider account to sort mail, summarise conversations and draft replies you review. Nothing is sent to AI until you allow it below."
              >
                <AiConsentCard />
                <Card>
                  <CardHeader
                    icon={<Bot className="h-5 w-5" />}
                    title="AI provider"
                    description="OpenAI, Anthropic (Claude), Google Gemini, OpenRouter or any OpenAI-compatible service. Paste an API key from your provider's dashboard."
                    actions={
                      <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setAiEditor(true)} disabled={!ai.data}>
                        Add provider
                      </Button>
                    }
                  />
                  <CardBody className="space-y-2">
                    {(ai.data?.providers ?? []).filter((p) => p.kind !== 'demo').length === 0 && ai.data?.builtin ? (
                      <div className="flex items-center gap-3 rounded-xl bg-raised px-3 py-2.5">
                        <Bot className="h-4 w-4 text-primary-ink" />
                        <span className="flex-1 text-[0.8125rem] font-medium text-ink">{ai.data.builtin.label} — included, no key needed</span>
                        <span className="text-xs text-muted">{ai.data.builtin.model}</span>
                      </div>
                    ) : (ai.data?.providers ?? []).filter((p) => p.kind !== 'demo').length === 0 ? (
                      <p className="text-[0.8125rem] text-muted">No provider yet. Without one, MailPilot still sorts mail by sender and keywords, but can't draft or summarise.</p>
                    ) : (
                      (ai.data?.providers ?? [])
                        .filter((p) => p.kind !== 'demo')
                        .map((p) => (
                          <div key={p.id} className="flex items-center gap-3 rounded-xl bg-raised px-3 py-2.5">
                            <Bot className="h-4 w-4 text-primary-ink" />
                            <span className="flex-1 text-[0.8125rem] font-medium text-ink">{p.label}</span>
                            <span className="text-xs text-muted">{p.defaultModel}</span>
                          </div>
                        ))
                    )}
                  </CardBody>
                </Card>
                {ai.data && <AiProviderEditor open={aiEditor} onClose={() => setAiEditor(false)} provider={null} kinds={ai.data.kinds.filter((k) => k.kind !== 'demo')} providers={ai.data.providers} />}
              </StepFrame>
            )}

            {step === 'notifications' && n && (
              <StepFrame title="Stay in the loop" description="New email and AI updates appear in a small island at the top of your screen. Choose what reaches you.">
                <Card>
                  <CardBody className="space-y-5">
                    <Switch size="lg" checked={n.island} onChange={(v) => update.mutate({ notifications: { ...n, island: v } })} label="Show the notification island" description="New email, AI drafts and automation updates, at the top centre of your screen." />
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Switch checked={n.newMail} disabled={!n.island} onChange={(v) => update.mutate({ notifications: { ...n, newMail: v } })} label="New email" />
                      <Switch checked={n.ai} disabled={!n.island} onChange={(v) => update.mutate({ notifications: { ...n, ai: v } })} label="AI drafts & insights" />
                      <Switch checked={n.sound} disabled={!n.island} onChange={(v) => update.mutate({ notifications: { ...n, sound: v } })} label="Soft chime" />
                      {!bridge() && ring.notificationPermission !== 'unsupported' && (
                        <div className="flex items-center justify-between gap-3 rounded-xl bg-raised px-3 py-2">
                          <span className="text-[0.8125rem] text-ink">Alerts when this tab is in the background</span>
                          {ring.notificationPermission === 'granted' ? (
                            <Badge tone="success">On</Badge>
                          ) : (
                            <Button size="sm" onClick={() => void ring.requestNotificationPermission()}>
                              Allow
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <Button size="sm" variant="outline" icon={<Sparkles className="h-4 w-4" />} onClick={() => void api('POST /api/notifications/test', { body: { kind: 'mail' } })}>
                      Show me an example
                    </Button>
                  </CardBody>
                </Card>
              </StepFrame>
            )}

            {step === 'team' && (
              <StepFrame title="Invite your team" description="Share this code or link with colleagues. They choose “Join a team” when they sign up. You can assign them to divisions afterwards in Settings → Organization & team.">
                <Card>
                  <CardBody className="space-y-4">
                    {invite ? (
                      <>
                        <div className="rounded-2xl border border-dashed border-primary/40 bg-primary-soft/40 p-5 text-center">
                          <p className="text-xs text-muted">Invite code · valid 14 days · up to 25 people</p>
                          <p className="mt-1 font-mono text-2xl font-semibold tracking-[0.2em] text-ink">{invite.code}</p>
                        </div>
                        <div className="flex flex-wrap justify-center gap-2">
                          <Button icon={<Copy className="h-4 w-4" />} onClick={() => void copyText(invite.code).then(() => toast.success('Code copied')).catch(() => toast.error('Could not copy — select the code and copy it manually.'))}>
                            Copy code
                          </Button>
                          {inviteLink(invite.code) && (
                            <Button icon={<Copy className="h-4 w-4" />} onClick={() => void copyText(inviteLink(invite.code)!).then(() => toast.success('Link copied')).catch(() => toast.error('Could not copy — select the link and copy it manually.'))}>
                              Copy link
                            </Button>
                          )}
                        </div>
                      </>
                    ) : (
                      <Button variant="primary" icon={<Users className="h-4 w-4" />} loading={createInvite.isPending} onClick={() => createInvite.mutate()}>
                        Create an invite
                      </Button>
                    )}
                  </CardBody>
                </Card>
              </StepFrame>
            )}

            {step === 'done' && (
              <StepFrame title="You're all set" description="Here's where things stand. Anything you skipped is waiting in Settings.">
                <Card>
                  <CardBody className="space-y-3">
                    {[
                      { ok: realMailboxes.length > 0, label: realMailboxes.length ? `${realMailboxes.length} email account${realMailboxes.length === 1 ? '' : 's'} connected` : 'No email account yet', to: '/settings/accounts' },
                      ...(isAdmin ? [{ ok: ((ai.data?.providers ?? []).some((p) => p.kind !== 'demo') || !!ai.data?.builtin) && !!me?.settings.ai.consentGivenAt, label: 'AI assistant', to: '/settings/ai' }] : []),
                      { ok: !!n?.island, label: n?.island ? 'Notification island on' : 'Notification island off', to: '/settings/notifications' },
                    ].map((r) => (
                      <button key={r.label} type="button" onClick={() => void finish(r.to)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-raised">
                        <span className={clsx('flex h-7 w-7 items-center justify-center rounded-full', r.ok ? 'bg-success-soft text-success-ink' : 'bg-warn-soft text-warn-ink')}>{r.ok ? <Check className="h-4 w-4" /> : '!'}</span>
                        <span className="flex-1 text-[0.875rem] text-ink">{r.label}</span>
                        {!r.ok && <span className="text-xs font-medium text-primary-ink">Set up</span>}
                      </button>
                    ))}
                  </CardBody>
                </Card>
                <div className="flex justify-center pt-2">
                  <Button size="lg" variant="primary" icon={<PartyPopper className="h-5 w-5" />} loading={update.isPending} onClick={() => void finish()} className="hover-glow">
                    Open my inbox
                  </Button>
                </div>
              </StepFrame>
            )}
          </motion.div>
        </AnimatePresence>

        {step !== 'done' && (
          <div className="mt-8 flex items-center justify-between">
            <Button icon={<ArrowLeft className="h-4 w-4" />} onClick={() => go(-1)} disabled={index === 0} className={clsx(index === 0 && 'invisible')}>
              Back
            </Button>
            <Button variant="primary" iconRight={<ArrowRight className="h-4 w-4" />} loading={demo.remove.isPending} onClick={() => void next()}>
              {step === 'email' && !realMailboxes.length ? 'Skip for now' : 'Continue'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
